import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as API from "../../framework/API/api";
import { utility } from '../../framework/utility/utilityProvider';
import { useTranslation } from '../../locale/useTranslation';
import BossDialog from '../../components/BossDialog';
import { EditPageHeader } from '../../components/EditPageHeader';
import AddRoundButton from '../../framework/forms/helpercomponents/buttons/AddRoundButton';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import RoundButton from '../../framework/forms/helpercomponents/buttons/RoundButton';
import { ConfirmDeleteAlert } from '../../ConfirmAlert';
import { ENTITYNAME, LOGEVENT, MODULE } from '../../framework/constant/constant';
import AddOutputsForm from './AddOutputsForm';

const DaiOutputForm = ({ item = {}, cancelEdit, refresh, setData }) => {
    const lang = useTranslation();

    const blankDataItem = {
        ...item,
        Name: item?.copy ? "Copy of " + item?.Name : item?.Name ?? "",
        Outputs: item?.Outputs ?? [],
        Archive: item?.Archive ?? false
    };

    const [dataItem, setDataItem] = useState(blankDataItem);
    const [showOutputPopup, setShowOutputPopup] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [selectedOutput, setSelectedOutput] = useState(null);

    const onChange = (e) => {
        if (e.target.name === 'Archive') {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        } else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    };

    useEffect(() => {
        if (item?.SID > 0) {
            loadEditData();
        }
    }, []);

    const loadEditData = () => {
        setDataItem({ ...item, Name: item?.copy ? "Copy of " + item?.Name : item?.Name ?? "" });
        setGridData(item?.Outputs ?? []);
    };

    const isValid = () => {
        if (!dataItem.Name || dataItem.Name === "") {
            toast.error(lang.please_enter_name_error_message, { position: toast.POSITION.TOP_RIGHT });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!isValid()) return;

        const finalData = {
            _id: dataItem?._id,
            SID: dataItem?.SID ?? 0,
            Name: dataItem?.Name,
            Outputs: gridData,
            Archive: dataItem?.Archive ?? false,
            checkDuplicate: true,
            query: [["Name", "=", dataItem?.Name]]
        };

        const res = await API.saveData(ENTITYNAME.DAI_OUTPUTS, finalData);
        if (res.success) {
            const logData = { event: res.message === "updated" ? LOGEVENT.UPDATE : LOGEVENT.INSERT, module: MODULE.DAI_OUTPUTS, data: res.data, message: res.message };
            API.SaveLogs(logData);
            utility.deleteLocalStorageItem(ENTITYNAME.DAI_OUTPUTS);
            refresh && refresh();
            cancelEdit();
            setData && setData(res.data);
        } else {
            toast.error(res.message, { position: toast.POSITION.TOP_RIGHT });
        }
    };

    const handleOutputSave = (item) => {
        const flattenedOutput = {
            ...item,
            AudioDescriptionName: item?.AudioDescriptions?.Name || "",
            VideoDescriptionName: item?.VideoDescriptions?.Name || "",
        };

        if (gridData.length > 0 && gridData.some((x, idx) =>
            x?.OutputName?.toLowerCase() === flattenedOutput.OutputName?.toLowerCase() &&
            idx !== item.index
        )
        ) {
            toast.error("Output Name already exists!", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        setGridData((prevGridData) => {
            let updatedData = [...prevGridData];
            if (item.index !== undefined && item.index !== null) {
                updatedData[item.index] = flattenedOutput;
            } else {
                updatedData.push(flattenedOutput);
            }
            return updatedData;
        });

        setShowOutputPopup(false);
        setSelectedOutput(null);
    };

    const handleOutputEdit = (item, index) => {
        setSelectedOutput({ ...item, index });
        setShowOutputPopup(true);
    };

    const handleOutputDelete = (index) => {
        ConfirmDeleteAlert(() => {
            const updated = [...gridData];
            updated.splice(index, 1);
            setGridData(updated);
        }, () => { });
    };

    const OutputCommandCell = (props) => (
        <td>
            <RoundButton icon="trash" Title={lang.delete_button_tooltip} onClick={() => handleOutputDelete(props.dataIndex)} />
            <RoundButton icon="pencil" Title={lang.edit_button_tooltip} onClick={() => handleOutputEdit(props.dataItem, props.dataIndex)} />
        </td>
    );

    return (
        <BossDialog title={item?.Title ?? lang.dai_output_dialog_header} onClose={cancelEdit} width="75vw" >
            <EditPageHeader title={dataItem?.Name || lang.dai_output_dialog_header} onSubmit={handleSubmit} onCancel={cancelEdit} />
            <div className="row mt-2">
                <div className="col-6">
                    <label>{lang.name_label} *</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        name="Name"
                        value={dataItem.Name}
                        onChange={onChange}
                    />
                </div>
                <div className="col-3" style={{ marginTop: "27px" }}>
                    <input
                        type="checkbox"
                        name={"Archive"}
                        checked={dataItem.Archive}
                        onChange={onChange}
                    />
                    <label className="pl-2" htmlFor="TabView">
                        {lang.archive}
                    </label>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-12">
                    <AddRoundButton onClick={() => { setShowOutputPopup(true); setSelectedOutput(null); }} />
                    <span className="float-right mt-3">{lang.outputs_label}</span>
                    <Grid data={gridData} style={{ height: "50vh", marginTop: "10px" }}>
                        <Column editable={false} cell={OutputCommandCell} width="100px" />
                        <Column field="OutputName" title="Output Name" />
                        <Column field="AudioDescriptions.Name" title="Video Desc Name" />
                        <Column field="VideoDescriptions.Name" title="Audio Desc Name" />
                        <Column field="VideoDescriptions.Height" title="Video Height" />
                        <Column field="VideoDescriptions.Width" title="Video Width" />
                        <Column field="VideoDescriptions.CodecSettings.H264Settings.Bitrate" title="Video Bitrate" />
                    </Grid>
                </div>
            </div>
            {showOutputPopup && <AddOutputsForm data={selectedOutput} cancelEdit={() => setShowOutputPopup(false)} onSave={handleOutputSave} />}
        </BossDialog>
    );
};

export default DaiOutputForm;
